import { tableFootprint, areaAtPoint, FLOOR_SNAP_CM, type TableShape, type FloorObject } from './floor-plan';

/**
 * Tidy up a floor.
 *
 * Deliberately NOT a language model. Spacing, alignment and walkways are a constraint
 * problem with exact answers, so solving them is instant, free, deterministic and
 * incapable of inventing a table. What a model is actually good at here — "'WC' reads as
 * staff shorthand, guests scan for 'Restrooms'" — is judgement about words, and that is a
 * separate feature.
 *
 * Two rules shape the whole design:
 *
 *  1. **It proposes, it never applies.** This returns a list of moves for a preview to
 *     draw. Silently rearranging a live venue's floor would be frightening.
 *  2. **Past a threshold it stops moving and starts talking.** Anything that wants to
 *     travel further than `maxMoveCm`, or that would land in a different area, is
 *     reverted and reported as a note. An owner's layout encodes things this code cannot
 *     see — where the power runs, which way the kitchen door swings — so a big move is a
 *     question, not a decision.
 */

export interface TidyOptions {
  /** Tables whose centres are within this of a shared axis get lined up exactly. */
  alignToleranceCm: number;
  /** A row is re-spaced only when its gaps differ by more than this. */
  spacingToleranceCm: number;
  /** Clearance kept between two tables' footprints — chair back to chair back. */
  minGapCm: number;
  /** Clearance kept between a table's footprint and a fixture. */
  fixtureGapCm: number;
  /** Beyond this, a move becomes a note instead. */
  maxMoveCm: number;
  /** A wall this close to square gets snapped to it. */
  squareToleranceDeg: number;
  /** Padding left around the tables a zone encloses. */
  zonePaddingCm: number;
  /** Fixtures stay put by default: the bar is where the plumbing is. */
  moveFixtures: boolean;
}

export const TIDY_DEFAULTS: TidyOptions = {
  alignToleranceCm: 30,
  spacingToleranceCm: 20,
  minGapCm: 45,
  fixtureGapCm: 30,
  maxMoveCm: 150,
  squareToleranceDeg: 4,
  zonePaddingCm: 45,
  moveFixtures: false,
};

export type TidyReason = 'aligned' | 'spaced' | 'walkway' | 'in-bounds' | 'squared' | 'zone-fit';
export type TidyIssue = 'too-far' | 'would-change-area' | 'no-room';

export interface TidyGeometry {
  x_cm: number;
  y_cm: number;
  width_cm: number;
  height_cm: number;
  rotation: number;
}

export interface TidyTable extends TidyGeometry {
  id: string;
  shape: TableShape;
  seats: number;
  area_id: string | null;
}

export interface TidyObject extends TidyGeometry {
  id: string;
  kind: FloorObject['kind'];
  subtype: string;
  area_id: string | null;
}

export interface TidyMove {
  id: string;
  kind: 'table' | 'object';
  from: TidyGeometry;
  to: TidyGeometry;
  reasons: TidyReason[];
}

export interface TidyNote {
  id: string;
  kind: 'table' | 'object';
  issue: TidyIssue;
  /** Already a sentence — the preview shows it as written. */
  message: string;
}

export interface TidyResult {
  moves: TidyMove[];
  notes: TidyNote[];
  counts: Record<TidyReason, number>;
}

export interface TidyInput {
  floor: { width_cm: number; height_cm: number };
  tables: TidyTable[];
  objects: TidyObject[];
  options?: Partial<TidyOptions>;
}

const snap = (v: number) => Math.round(v / FLOOR_SNAP_CM) * FLOOR_SNAP_CM;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

interface Work extends TidyTable {
  ox: number;
  oy: number;
  reasons: Set<TidyReason>;
}

/** Half-extents of a table's footprint — the top plus everyone seated at it. */
function halfSpan(t: TidyTable): { hw: number; hh: number } {
  const f = tableFootprint({ ...t, seats: t.seats, shape: t.shape });
  return { hw: f.width / 2, hh: f.height / 2 };
}

/**
 * Group values that sit within `tol` of each other.
 *
 * Sorted first and cut wherever the step exceeds the tolerance, so the grouping depends
 * only on the numbers — not on the order rows came back from the database.
 */
function cluster(values: Array<{ id: string; v: number }>, tol: number): string[][] {
  const sorted = [...values].sort((a, b) => a.v - b.v || a.id.localeCompare(b.id));
  const out: string[][] = [];
  let run: Array<{ id: string; v: number }> = [];
  for (const item of sorted) {
    if (run.length && item.v - run[run.length - 1]!.v > tol) { out.push(run.map(r => r.id)); run = []; }
    run.push(item);
  }
  if (run.length) out.push(run.map(r => r.id));
  return out;
}

export function tidyFloor(input: TidyInput): TidyResult {
  const o: TidyOptions = { ...TIDY_DEFAULTS, ...input.options };
  const notes: TidyNote[] = [];
  const counts: Record<TidyReason, number> = {
    aligned: 0, spaced: 0, walkway: 0, 'in-bounds': 0, squared: 0, 'zone-fit': 0,
  };

  // Sorted by id throughout: the same floor must always tidy to the same plan.
  const work: Work[] = [...input.tables]
    .sort((a, b) => a.id.localeCompare(b.id))
    .map(t => ({ ...t, ox: t.x_cm, oy: t.y_cm, reasons: new Set<TidyReason>() }));
  const byId = new Map(work.map(w => [w.id, w]));

  const fixtures = input.objects.filter(x => x.kind === 'fixture');
  const solidish = (t: Work) => {
    const { hw, hh } = halfSpan(t);
    return { x: t.x_cm, y: t.y_cm, hw, hh };
  };

  const keepInBounds = (t: Work) => {
    const { hw, hh } = halfSpan(t);
    const nx = clamp(t.x_cm, hw, Math.max(hw, input.floor.width_cm - hw));
    const ny = clamp(t.y_cm, hh, Math.max(hh, input.floor.height_cm - hh));
    if (nx !== t.x_cm || ny !== t.y_cm) { t.x_cm = nx; t.y_cm = ny; t.reasons.add('in-bounds'); }
  };

  /**
   * Push overlapping pairs apart along whichever axis needs the least travel.
   *
   * Every push is rounded UP to a whole grid step and positions are kept on the grid
   * throughout. Separating in continuous space and snapping at the end looked right and
   * was not: rounding two tables towards each other could close up to 25 cm of the gap
   * that had just been opened, so a "tidied" floor came back with tables 33 cm apart
   * when the minimum was 45.
   */
  const step = (need: number) => Math.ceil(need / 2 / FLOOR_SNAP_CM) * FLOOR_SNAP_CM;

  const separate = (gap: number, tag: TidyReason) => {
    // Bringing everything onto the grid first is what makes the guarantee above hold,
    // and it IS a change the owner will see — so it is recorded, not done quietly. A
    // preview that under-reports its own moves is worse than no preview.
    work.forEach(t => {
      const sx = snap(t.x_cm), sy = snap(t.y_cm);
      if (Math.abs(sx - t.x_cm) >= 1 || Math.abs(sy - t.y_cm) >= 1) t.reasons.add('aligned');
      t.x_cm = sx; t.y_cm = sy;
    });

    for (let pass = 0; pass < 16; pass++) {
      let moved = false;
      for (let i = 0; i < work.length; i++) {
        for (let j = i + 1; j < work.length; j++) {
          const a = solidish(work[i]!), b = solidish(work[j]!);
          const dx = b.x - a.x, dy = b.y - a.y;
          const needX = a.hw + b.hw + gap - Math.abs(dx);
          const needY = a.hh + b.hh + gap - Math.abs(dy);
          if (needX <= 0 || needY <= 0) continue;

          if (needX < needY) {
            const push = step(needX) * (dx === 0 ? 1 : Math.sign(dx));
            work[i]!.x_cm -= push; work[j]!.x_cm += push;
          } else {
            const push = step(needY) * (dy === 0 ? 1 : Math.sign(dy));
            work[i]!.y_cm -= push; work[j]!.y_cm += push;
          }
          work[i]!.reasons.add(tag); work[j]!.reasons.add(tag);
          moved = true;
        }
      }
      // Fixtures are immovable, so a table sharing space with one is the one that yields.
      for (const t of work) {
        for (const f of fixtures) {
          const { hw, hh } = halfSpan(t);
          const dx = t.x_cm - f.x_cm, dy = t.y_cm - f.y_cm;
          const needX = hw + f.width_cm / 2 + o.fixtureGapCm - Math.abs(dx);
          const needY = hh + f.height_cm / 2 + o.fixtureGapCm - Math.abs(dy);
          if (needX <= 0 || needY <= 0) continue;
          if (needX < needY) t.x_cm += step(needX * 2) * (dx === 0 ? 1 : Math.sign(dx));
          else t.y_cm += step(needY * 2) * (dy === 0 ? 1 : Math.sign(dy));
          t.reasons.add(tag);
          moved = true;
        }
        keepInBounds(t);
        t.x_cm = snap(t.x_cm); t.y_cm = snap(t.y_cm);
      }
      if (!moved) break;
    }
  };

  // ── 1. make it valid ──────────────────────────────────────────────────────
  work.forEach(keepInBounds);
  separate(o.minGapCm, 'walkway');

  // ── 2. make it tidy: line rows and columns up exactly ─────────────────────
  const snapshot = () => work.map(w => ({ id: w.id, x: w.x_cm, y: w.y_cm }));
  const beforeAlign = snapshot();

  for (const axis of ['y', 'x'] as const) {
    const groups = cluster(work.map(w => ({ id: w.id, v: axis === 'x' ? w.x_cm : w.y_cm })), o.alignToleranceCm);
    for (const ids of groups) {
      if (ids.length < 2) continue;
      const members = ids.map(id => byId.get(id)!);
      const mean = members.reduce((s, m) => s + (axis === 'x' ? m.x_cm : m.y_cm), 0) / members.length;
      const target = snap(mean);
      for (const m of members) {
        if (Math.abs((axis === 'x' ? m.x_cm : m.y_cm) - target) < 0.5) continue;
        if (axis === 'x') m.x_cm = target; else m.y_cm = target;
        m.reasons.add('aligned');
      }
    }
  }

  // ── 3. even gaps along each row ───────────────────────────────────────────
  const rows = cluster(work.map(w => ({ id: w.id, v: w.y_cm })), 1);
  for (const ids of rows) {
    if (ids.length < 3) continue;
    const row = ids.map(id => byId.get(id)!).sort((a, b) => a.x_cm - b.x_cm || a.id.localeCompare(b.id));
    const spans = row.map(halfSpan);
    const left = row[0]!.x_cm - spans[0]!.hw;
    const right = row[row.length - 1]!.x_cm + spans[spans.length - 1]!.hw;
    const totalWidth = spans.reduce((s, sp) => s + sp.hw * 2, 0);
    const gap = (right - left - totalWidth) / (row.length - 1);
    if (gap < o.minGapCm) continue;   // no room to even out; leave it alone

    const gaps: number[] = [];
    for (let i = 1; i < row.length; i++) {
      gaps.push((row[i]!.x_cm - spans[i]!.hw) - (row[i - 1]!.x_cm + spans[i - 1]!.hw));
    }
    if (Math.max(...gaps) - Math.min(...gaps) <= o.spacingToleranceCm) continue;

    let cursor = left;
    row.forEach((t, i) => {
      const target = snap(cursor + spans[i]!.hw);
      if (Math.abs(target - t.x_cm) > 0.5) { t.x_cm = target; t.reasons.add('spaced'); }
      cursor += spans[i]!.hw * 2 + gap;
    });
  }

  // ── 4. tidiness must not have broken validity ─────────────────────────────
  // Anything that now overlaps gets its alignment and spacing rolled back rather than
  // being shoved somewhere new: a slightly crooked row beats two tables in one spot.
  const beforeById = new Map(beforeAlign.map(b => [b.id, b]));
  for (let i = 0; i < work.length; i++) {
    for (let j = i + 1; j < work.length; j++) {
      const a = solidish(work[i]!), b = solidish(work[j]!);
      if (Math.abs(b.x - a.x) >= a.hw + b.hw + o.minGapCm) continue;
      if (Math.abs(b.y - a.y) >= a.hh + b.hh + o.minGapCm) continue;
      for (const t of [work[i]!, work[j]!]) {
        const prev = beforeById.get(t.id);
        if (!prev) continue;
        t.x_cm = prev.x; t.y_cm = prev.y;
        t.reasons.delete('aligned'); t.reasons.delete('spaced');
      }
    }
  }

  // ── 5. walls that are nearly square become square ─────────────────────────
  const objectMoves: TidyMove[] = [];
  for (const obj of [...input.objects].sort((a, b) => a.id.localeCompare(b.id))) {
    if (obj.kind !== 'wall') continue;
    const nearest = Math.round(obj.rotation / 90) * 90;
    const off = Math.abs(obj.rotation - nearest);
    if (off === 0 || off > o.squareToleranceDeg) continue;
    const to = { ...obj, rotation: ((nearest % 360) + 360) % 360 };
    objectMoves.push({
      id: obj.id, kind: 'object',
      from: { x_cm: obj.x_cm, y_cm: obj.y_cm, width_cm: obj.width_cm, height_cm: obj.height_cm, rotation: obj.rotation },
      to: { x_cm: to.x_cm, y_cm: to.y_cm, width_cm: to.width_cm, height_cm: to.height_cm, rotation: to.rotation },
      reasons: ['squared'],
    });
    counts.squared++;
  }

  // ── 6. zones hug the tables they hold ─────────────────────────────────────
  const zones = input.objects.filter(z => z.kind === 'zone');
  for (const z of [...zones].sort((a, b) => a.id.localeCompare(b.id))) {
    if (!z.area_id) continue;
    const held = work.filter(t => t.area_id === z.area_id);
    if (!held.length) continue;

    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const t of held) {
      const { hw, hh } = halfSpan(t);
      minX = Math.min(minX, t.x_cm - hw); maxX = Math.max(maxX, t.x_cm + hw);
      minY = Math.min(minY, t.y_cm - hh); maxY = Math.max(maxY, t.y_cm + hh);
    }
    const w = snap(maxX - minX + o.zonePaddingCm * 2);
    const h = snap(maxY - minY + o.zonePaddingCm * 2);
    const x = snap((minX + maxX) / 2);
    const y = snap((minY + maxY) / 2);
    if (x === z.x_cm && y === z.y_cm && w === z.width_cm && h === z.height_cm) continue;

    objectMoves.push({
      id: z.id, kind: 'object',
      from: { x_cm: z.x_cm, y_cm: z.y_cm, width_cm: z.width_cm, height_cm: z.height_cm, rotation: z.rotation },
      to: { x_cm: x, y_cm: y, width_cm: w, height_cm: h, rotation: z.rotation },
      reasons: ['zone-fit'],
    });
    counts['zone-fit']++;
  }

  // ── 7. the guardrails ─────────────────────────────────────────────────────
  // Zones may have moved, so area membership is checked against the FINAL ones.
  const finalZones: FloorObject[] = zones.map(z => {
    const moved = objectMoves.find(m => m.id === z.id);
    const g = moved ? moved.to : z;
    return {
      id: z.id, venue_id: '', floor_id: '', kind: 'zone', subtype: z.subtype, label: null,
      x_cm: g.x_cm, y_cm: g.y_cm, width_cm: g.width_cm, height_cm: g.height_cm,
      rotation: g.rotation, z_index: 0, area_id: z.area_id, color: null,
      created_at: '', updated_at: '',
    };
  });
  const hasNamedZone = finalZones.some(z => z.area_id);

  const moves: TidyMove[] = [];
  for (const t of work) {
    const nx = snap(t.x_cm), ny = snap(t.y_cm);
    const travelled = Math.hypot(nx - t.ox, ny - t.oy);
    if (travelled < 1 || t.reasons.size === 0) continue;

    // A table that overlaps its neighbour or hangs off the floor is not a matter of
    // taste, so clearing it is allowed to travel further than a cosmetic nudge. The
    // ceiling is still there: past it, the solver stops guessing and says so.
    const fixingValidity = t.reasons.has('walkway') || t.reasons.has('in-bounds');
    const allowance = fixingValidity ? o.maxMoveCm * 3 : o.maxMoveCm;
    if (travelled > allowance) {
      notes.push({
        id: t.id, kind: 'table', issue: 'too-far',
        message: `Would have to move ${(travelled / 100).toFixed(1)} m to clear its neighbours — left where it is.`,
      });
      continue;
    }
    if (hasNamedZone) {
      const was = areaAtPoint(finalZones, t.ox, t.oy);
      const now = areaAtPoint(finalZones, nx, ny);
      if (was !== now) {
        notes.push({
          id: t.id, kind: 'table', issue: 'would-change-area',
          message: 'Tidying would move it into a different area, which changes what a guest pays — left where it is.',
        });
        continue;
      }
    }

    const reasons = [...t.reasons].sort();
    reasons.forEach(r => { counts[r]++; });
    moves.push({
      id: t.id, kind: 'table',
      from: { x_cm: t.ox, y_cm: t.oy, width_cm: t.width_cm, height_cm: t.height_cm, rotation: t.rotation },
      to: { x_cm: nx, y_cm: ny, width_cm: t.width_cm, height_cm: t.height_cm, rotation: t.rotation },
      reasons,
    });
  }

  return { moves: [...moves, ...objectMoves], notes, counts };
}

/** One line per thing it did, for the preview's summary. */
export function describeTidy(result: TidyResult): string[] {
  const { counts } = result;
  const lines: string[] = [];
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

  if (counts.aligned) lines.push(`Lined up ${plural(counts.aligned, 'table', 'tables')}`);
  if (counts.spaced) lines.push(`Evened the gaps around ${plural(counts.spaced, 'table', 'tables')}`);
  if (counts.walkway) lines.push(`Opened space around ${plural(counts.walkway, 'table', 'tables')}`);
  if (counts['in-bounds']) lines.push(`Brought ${plural(counts['in-bounds'], 'table', 'tables')} back inside the room`);
  if (counts.squared) lines.push(`Squared ${plural(counts.squared, 'wall', 'walls')}`);
  if (counts['zone-fit']) lines.push(`Fitted ${plural(counts['zone-fit'], 'area', 'areas')} to the tables inside`);
  return lines;
}
